import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_USER_PROFILES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { DELETE_USER_PROFILE_MUTATION } from '../../integrations/allkort/graphql/gql/UserProfileMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { DeleteUserProfileResponse, DeletedUserProfile } from '../../integrations/allkort/graphql/interface/UserProfile';

/**
 * useDeleteUserProfile
 *
 * Custom hook for deleting a user profile (sub-user) from the customer's account.
 * Fetches the current customer's ID from cache automatically.
 *
 * @example
 * ```tsx
 * const { deleteUserProfile, loading, error } = useDeleteUserProfile();
 *
 * await deleteUserProfile(285508);
 * ```
 */
export const useDeleteUserProfile = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'cache-first' }
	);

	const [deleteUserProfileMutation, { loading, error, data }] = useMutation<DeleteUserProfileResponse>(
		DELETE_USER_PROFILE_MUTATION,
		{
			refetchQueries: [{ query: GET_USER_PROFILES_QUERY }],
			awaitRefetchQueries: true,
		}
	);

	const deleteUserProfile = async (userProfileId: number): Promise<DeletedUserProfile | null> => {
		const customerId = customerData?.currentCustomer?.id;
		if (!customerId) {
			throw new Error('Customer ID not available');
		}

		try {
			const result = await deleteUserProfileMutation({
				variables: { customerId, userProfileId },
			});
			return result.data?.deleteUserProfile?.userProfile ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { deleteUserProfile, loading, error, data };
};
